import React from "react";
import ReactDOM from "react-dom";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import { BrowserRouter } from "react-router-dom";
// import LoaderProvider from "./main/LoaderProvider";
import ErrorBoundary from "./ErrorBoundary";
import StackTrace from "stacktrace-js";
import { HelmetProvider } from "react-helmet-async";
import SimpleLoaderProvider from "./main/SimpleLoaderProvider";

// 🌍 Determine environment based on hostname
const hostname = window.location.hostname;
let envType = "PRODUCTION";
if (hostname === "localhost") envType = "LOCAL";
else if (hostname.includes("stage")) envType = "STAGE";

window.quote = "Rate Lelo";
window.inqurey = "Rate Lelo";
window.zero_price = "Contact for Price";
const excludedPaths = ["/login", "/signup", "/forgotten", "/otp"];
window.isExcluded = excludedPaths.includes(window.location.pathname);

window.getenv = (key) => process.env[`REACT_APP_${key}_${envType}`] || "";

// 🔥 Crash-aware redirect condition
const shouldRedirectOnCrash = (message, stackframes = []) => {
  return (
    //envType !== "LOCAL" &&
    stackframes.length >= 3 &&
    typeof message === "string" &&
    !message.toLowerCase().includes("script error") &&
    !message.toLowerCase().includes("network error")
  );
};

// ✅ Ignore errors caused by Chrome extensions
const isExtensionError = (message, source, error) => {
  return (
    source?.startsWith("chrome-extension://") ||
    message?.includes("chrome-extension://") ||
    error?.stack?.includes("chrome-extension://")
  );
};

// 🔥 Global error handler
window.onerror = (message, source, lineno, colno, error) => {
  if (isExtensionError(message, source, error)) return true;

  const isTawkError = typeof message === "string" && message.includes("i18next is not a function");
  if (isTawkError) return true;

  const form = new FormData();
  form.append("val", "in");
  form.append("type", "js-error");
  form.append("message", String(message));
  form.append("source", source);
  form.append("lineno", lineno || 0);
  form.append("colno", colno || 0);
  form.append("url", window.location.href);
  form.append("user_agent", navigator.userAgent);

  const fallbackLog = (fallback) => {
    form.append("stack", fallback);
    sessionStorage.setItem("lastErrorInfo", JSON.stringify({ message, stack: fallback }));
    if (envType !== "LOCAL") {
      axios.post("/pro/logs/errors.php", form);
    }
  };

  if (!error || typeof error !== "object" || !(error instanceof Error) || !error.stack) {
    fallbackLog("Non-object or incomplete error: " + String(error));
    return true;
  }

  try {
    StackTrace.fromError(error)
      .then((stackframes) => {
        const stack = stackframes.map((f) => f.toString()).join("\n");
        form.append("stack", stack);
        sessionStorage.setItem("lastErrorInfo", JSON.stringify({ message, stack }));

        if (shouldRedirectOnCrash(message, stackframes)) {
          axios.post("/pro/logs/errors.php", form).finally(() => {
            if (envType !== "LOCAL") {
              window.location.href = "/error-system";
            }
          });
        } else {
          axios.post("/pro/logs/errors.php", form);
        }
      })
      .catch((traceErr) => {
        fallbackLog("StackTrace.fromError() failed: " + String(traceErr));
      });
  } catch (e) {
    fallbackLog("StackTrace Exception: " + String(e));
  }

  return true;
};

// 🔥 window.onunhandledrejection handler
window.onunhandledrejection = (event) => {
  const reason = event.reason;
  if (isExtensionError(reason?.message, "", reason)) return true;

  const isTawkError =
    reason?.message && typeof reason.message === "string" && reason.message.includes("i18next");

  if (isTawkError) return true;

  const form = new FormData();
  form.append("val", "in");
  form.append("type", "promise-rejection");
  form.append("message", String(reason?.message || "Unhandled rejection"));
  form.append("url", window.location.href);
  form.append("user_agent", navigator.userAgent);

  const fallbackLog = (fallback) => {
    form.append("stack", fallback);
    sessionStorage.setItem("lastErrorInfo", JSON.stringify({ message: reason?.message, stack: fallback }));
    if (envType !== "LOCAL") {
      axios.post("/pro/logs/errors.php", form);
    }
  };

  if (!reason || typeof reason !== "object" || !(reason instanceof Error) || !reason.stack) {
    fallbackLog("Non-object or incomplete rejection: " + String(reason));
    return true;
  }

  try {
    StackTrace.fromError(reason)
      .then((stackframes) => {
        const stack = stackframes.map((f) => f.toString()).join("\n");
        form.append("stack", stack);
        sessionStorage.setItem("lastErrorInfo", JSON.stringify({ message: reason?.message, stack }));

        if (shouldRedirectOnCrash(reason?.message, stackframes)) {
          axios.post("/pro/logs/errors.php", form).finally(() => {
            if (envType !== "LOCAL") {
              window.location.href = "/error-system";
            }
          });
        } else {
          axios.post("/pro/logs/errors.php", form);
        }
      })
      .catch((traceErr) => {
        fallbackLog("StackTrace.fromError() failed: " + String(traceErr));
      });
  } catch (e) {
    fallbackLog("StackTrace Exception: " + String(e));
  }

  return true;
};

// 🌐 Axios base URL for localhost
if (window.location.hostname === "localhost") {
  axios.defaults.baseURL = "http://localhost";
}

// 🧭 Panel setup
const buyerPaths = ["/buyer", "/product-buy-request", "/requestno"];
const currentPath = window.location.pathname;
localStorage.setItem("panel", buyerPaths.some((path) => currentPath.startsWith(path)) ? "buyer" : "seller");

const PLACEHOLDER = "/placeholder.avif"; // Should be inside /public

// ✅ Handle <img> tags
const setImageErrorHandler = (img) => {
  if (!img.dataset.hasErrorHandler) {
    img.onerror = function () {
      this.onerror = null;
      this.src = PLACEHOLDER;
    };
    img.dataset.hasErrorHandler = "true";
  }
};

// ✅ Handle divs with background-image
const handleBackgroundImage = (el) => {
  const style = window.getComputedStyle(el);
  const bgImage = style.backgroundImage;

  if (bgImage && bgImage !== "none") {
    const urlMatch = bgImage.match(/url\("?(.+?)"?\)/);
    if (urlMatch && urlMatch[1]) {
      const img = new Image();
      img.src = urlMatch[1];

      img.onerror = () => {
        el.style.backgroundImage = `url('${PLACEHOLDER}')`;
      };
    }
  }
};

const observeImages = () => {
  // Handle existing images and divs with background-image
  document.querySelectorAll("img").forEach(setImageErrorHandler);
  document.querySelectorAll(".supplier-avatar").forEach(handleBackgroundImage);

  // MutationObserver to track DOM changes
  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === "childList") {
        mutation.addedNodes.forEach((node) => {
          if (node.tagName === "IMG") {
            setImageErrorHandler(node);
          } else if (node.querySelectorAll) {
            // Handle images
            node.querySelectorAll("img").forEach(setImageErrorHandler);

            // Handle supplier-avatar divs
            node.querySelectorAll(".supplier-avatar").forEach(handleBackgroundImage);
          }
        });
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

window.addEventListener("DOMContentLoaded", observeImages);


// 🚀 Mount App
ReactDOM.render(
  <React.StrictMode>
    <BrowserRouter>
      <SimpleLoaderProvider>
        <ErrorBoundary>
          <HelmetProvider>
            <App />
          </HelmetProvider>
        </ErrorBoundary>
      </SimpleLoaderProvider>
    </BrowserRouter>
  </React.StrictMode>,
  document.getElementById("root")
);

reportWebVitals();
